// Pricing calculation helpers (pure functions)

export const SIZE_LABELS = {
  small: { en: "Small", zh: "小型车", sub: "Compact / Hatch · 小轿车/掀背", token: "sky" },
  medium: { en: "Medium", zh: "中型车", sub: "Sedan / Coupe · 轿车/轿跑", token: "amber" },
  large: { en: "Large", zh: "大型车", sub: "SUV / Truck / Van · SUV/皮卡", token: "orange" },
};

export const REPAIR_LABELS = {
  none: { en: "No Repair", zh: "无修整", short: "None" },
  small: { en: "Minor Repair", zh: "小修", short: "Minor" },
  medium: { en: "Medium Repair", zh: "中修", short: "Medium" },
  large: { en: "Major Repair", zh: "大修", short: "Major" },
};

export const REPAIR_TIERS = ["none", "small", "medium", "large"];

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// { min, max } for a tier; max null = open-ended. none → null
export function repairRange(settings, tier) {
  if (!tier || tier === "none") return null;
  const r = settings.repair_ranges?.[tier];
  if (!r) return null;
  const max = r.max === null || r.max === undefined || r.max === "" ? null : Number(r.max);
  return { min: Number(r.min ?? 0), max };
}

export function clampRepair(range, value) {
  if (!range) return 0;
  let v = Number(value);
  if (!Number.isFinite(v)) v = range.min;
  v = Math.max(range.min, v);
  if (range.max !== null) v = Math.min(range.max, v);
  return round2(v);
}

export const formatRange = (sym, range) =>
  range ? (range.max === null ? `${sym}${range.min}+` : `${sym}${range.min}–${range.max}`) : "";

export function touchupRange(settings) {
  const r = settings.touchup_range || { min: 60, max: 120 };
  return { min: Number(r.min ?? 0), max: r.max === null || r.max === undefined || r.max === "" ? null : Number(r.max) };
}

export const TOUCHUP_LABEL = { en: "Touch-up Respray", zh: "局部补漆", short: "Touch-up" };

// Effective repair price for a panel selection (custom price clamped to the tier's range)
export function repairPriceFor(settings, sel) {
  const range = repairRange(settings, sel.repair);
  if (!range) return 0;
  return clampRepair(range, sel.repairPrice ?? range.min);
}

// Unit respray price for a panel at a given size (manual per-size price or base × multiplier)
export function panelUnitPrice(settings, panel, size) {
  const mult = settings.size_multipliers?.[size] ?? 1;
  if (settings.pricing_mode === "manual") {
    if (panel.custom || !panel.prices) return round2(Number(panel.base_price ?? 0));
    return round2(Number(panel.prices[size] ?? panel.base_price ?? 0));
  }
  return round2(Number(panel.base_price) * mult);
}

function specialSurcharge(settings, respray, qty) {
  const fx = settings.special_effect || {};
  if (fx.mode === "percent") return round2(respray * (Number(fx.percentage_rate ?? 0) / 100));
  return Number(fx.fixed_rate_per_panel ?? 0) * qty;
}

function sprayUnitPrice(settings, panel, sel, size) {
  if (sel.sprayMode === "touchup") {
    const range = touchupRange(settings);
    return clampRepair(range, sel.touchupPrice ?? range.min);
  }
  if (sel.resprayPrice !== null && sel.resprayPrice !== undefined) return round2(Number(sel.resprayPrice)); // legacy quote re-opened: keep its saved figure
  return panelUnitPrice(settings, panel, size);
}

function panelLine(settings, panel, sel, size) {
  const qty = panel.per_unit ? Number(sel.qty || 1) : 1;
  const respray = round2(sprayUnitPrice(settings, panel, sel, size) * qty);
  const repair = round2(repairPriceFor(settings, sel) * qty);
  const special = sel.special ? specialSurcharge(settings, respray, qty) : 0;
  return { key: panel.key, en: panel.en, zh: panel.zh || "", qty, respray, sprayMode: sel.sprayMode || "full", repair, repairTier: sel.repair, special, total: round2(respray + repair + special) };
}

// services: { [id]: price } — key present = selected; price entered on the quote (falls back to default)
function serviceLines(settings, services) {
  return (settings.services || [])
    .filter((s) => services[s.id] !== undefined && services[s.id] !== false)
    .map((s) => {
      const custom = services[s.id];
      const price = custom === true || custom === "" || custom === null ? Number(s.price) || 0 : Number(custom) || 0;
      return { id: s.id, en: s.en, zh: s.zh, price: round2(price) };
    });
}

function applyDiscount(subtotal, discount) {
  const mode = discount?.mode === "fixed" ? "fixed" : "percent";
  const value = Math.max(0, Number(discount?.value) || 0);
  const raw = mode === "percent" ? round2(subtotal * (value / 100)) : round2(value);
  const amount = Math.min(raw, subtotal);
  return { mode, value, amount, afterDiscount: round2(subtotal - amount) };
}

const sum = (arr, k) => round2(arr.reduce((s, x) => s + Number(x[k] || 0), 0));

// ---------- Full car respray (Section 6) ----------
export const FULL_SIZES = [["small", "Small", "小型"], ["medium", "Medium", "中型"], ["large", "Large", "大型"], ["xl", "XL / Sports", "特大/跑车"]];
export const FULL_SCOPES = { exterior: { en: "Exterior", zh: "外部" }, interior_exterior: { en: "Interior + Exterior", zh: "内外全喷" } };
export const FULL_TIERS = { standard: { en: "Standard", zh: "标准" }, premium: { en: "Premium", zh: "高级" } };
export const EMPTY_FULL_RESPRAY = { scope: "off", tier: "standard", size: "medium", colorChange: false, paintType: "solid", priceOverride: null, paintSurcharge: "", complexitySurcharge: "" };

export function fullResprayBase(settings, fr) {
  return Number(settings.full_respray?.prices?.[fr.tier]?.[fr.scope]?.[fr.size] ?? 0);
}

export function fullResprayColorChange(settings, fr) {
  if (!fr.colorChange || fr.scope !== "exterior" || fr.paintType !== "solid") return 0; // free on interior+exterior; tricoat is priced manually
  const cc = settings.full_respray?.color_change || {};
  return Number((fr.size === "small" || fr.size === "medium" ? cc.small_medium : cc.large_xl) ?? 0);
}

// One line item that joins the services subtotal (discountable)
export function fullResprayItem(settings, fr) {
  if (!fr || fr.scope === "off" || !settings.full_respray) return null;
  const base = fr.priceOverride !== null && fr.priceOverride !== undefined && fr.priceOverride !== "" ? Number(fr.priceOverride) || 0 : fullResprayBase(settings, fr);
  const colorChange = fullResprayColorChange(settings, fr);
  const paintSurcharge = round2(Number(fr.paintSurcharge) || 0);
  const complexity = round2(Number(fr.complexitySurcharge) || 0);
  const size = FULL_SIZES.find((s) => s[0] === fr.size) || FULL_SIZES[1];
  const paint = fr.paintType === "tricoat" ? "Tricoat / Pearl / Custom" : "Solid / Metallic";
  const details = [fr.colorChange ? `Colour change (${paint})` : `Same colour (${paint})`];
  if (colorChange > 0) details.push(`colour change +${settings.currency_symbol || "$"}${colorChange}`);
  if (paintSurcharge > 0) details.push(`paint surcharge +${settings.currency_symbol || "$"}${paintSurcharge}`);
  if (complexity > 0) details.push(`complexity +${settings.currency_symbol || "$"}${complexity}`);
  details.push(settings.full_respray.downtime?.[fr.scope] || "");
  return {
    id: "full_respray", kind: "full_respray",
    en: `Full Car Respray 全车喷漆 — ${FULL_TIERS[fr.tier]?.en || ""} · ${FULL_SCOPES[fr.scope]?.en || ""} · ${size[1]}`,
    zh: details.filter(Boolean).join(" · "),
    price: round2(base + colorChange + paintSurcharge + complexity),
    base, colorChange, paintSurcharge, complexity,
  };
}

// customServices: [{ id, en, price }] ad-hoc jobs typed on the quote
// parts: [{ id, name, price, ri }] spare parts — net pricing, excluded from discount
export function calculateQuote({ settings, size, selections, customPanels, services, customServices = [], parts = [], discount, fullRespray = null }) {
  if (!settings) return null;
  const fullItem = fullResprayItem(settings, fullRespray);
  const allPanels = [...(settings.panels || []), ...(customPanels || [])];
  const lineItems = allPanels
    .filter((p) => selections[p.key]?.selected)
    .map((p) => panelLine(settings, p, selections[p.key], size));
  const serviceItems = [
    ...(fullItem ? [fullItem] : []),
    ...serviceLines(settings, services),
    ...customServices.map((s) => ({ id: s.id, en: s.en, zh: "", price: round2(Number(s.price) || 0), custom: true })),
  ];
  const partItems = parts.map((p) => {
    const price = round2(Number(p.price) || 0);
    const ri = round2(Number(p.ri) || 0);
    return { id: p.id, name: p.name, price, ri, total: round2(price + ri) };
  });

  const respraySubtotal = sum(lineItems, "respray");
  const repairSubtotal = sum(lineItems, "repair");
  const specialSubtotal = sum(lineItems, "special");
  const servicesSubtotal = sum(serviceItems, "price");
  const partsSubtotal = sum(partItems, "total");
  const subtotal = round2(respraySubtotal + repairSubtotal + specialSubtotal + servicesSubtotal);

  const disc = applyDiscount(subtotal, discount);
  const taxable = round2(disc.afterDiscount + partsSubtotal);
  const taxRate = Number(settings.tax_rate_percent ?? 0);
  const tax = round2(taxable * (taxRate / 100));

  return {
    mult: settings.size_multipliers?.[size] ?? 1,
    manual: settings.pricing_mode === "manual",
    mode: settings.special_effect?.mode ?? "fixed",
    lineItems,
    serviceItems,
    partItems,
    respraySubtotal,
    repairSubtotal,
    specialSubtotal,
    servicesSubtotal,
    partsSubtotal,
    subtotal,
    discountMode: disc.mode,
    discountValue: disc.value,
    discountAmount: disc.amount,
    afterDiscount: disc.afterDiscount,
    taxRate,
    tax,
    total: round2(taxable + tax),
    panelCount: lineItems.length,
  };
}

export const isEmptyQuote = (quote) => !quote || (quote.panelCount === 0 && quote.serviceItems.length === 0 && !(quote.partItems || []).length);

export function formatMoney(symbol, n) {
  const val = Number(n || 0);
  return `${symbol}${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
